'use client';

import React, { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, Flame, Zap, FormInput, Code, CheckCircle2, 
  ChevronDown, Share2, Sparkles, UserPlus, Trophy
} from 'lucide-react'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { ShinyButton } from '@/components/ui/shiny-button'
import { DashboardPreview } from '@/components/ui/dashboard-animation'

export default function StartupsClient() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const faqs = [
    {
      q: "How does the viral milestone waitlist refer-a-friend loop work?",
      a: "When a user signs up on your waitlist, they are assigned a queue position and a unique referral URL. As they share their link and friends sign up, their queue position improves and they unlock customizable milestone rewards (like free months or early access)."
    },
    {
      q: "What is the redirect latency for shortened links?",
      a: "Our smart link redirect pipeline is hosted on global edge servers, returning 301/302 responses in under 100ms. This ensures that you don't lose leads or waste Google Ad click budget on slow pages."
    },
    {
      q: "Can we integrate referral milestones into our custom product?",
      a: "Yes. Lenklyst is built developer-first. Generate REST API keys and setup webhook event listeners in the dashboard to sync referral updates directly with your backend database."
    },
    {
      q: "What integrations do you support?",
      a: "We support webhook integration, allowing you to automatically sync waitlist signups to tools like Slack, email marketing apps, or CRM dashboards."
    }
  ]



  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30 text-white font-sans">
      <GeometricBackground />
      <MarketingNavbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 max-w-[1400px] mx-auto z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center min-h-[70vh]">
          {/* Left Column: Hero Copy */}
          <div className="lg:col-span-7 space-y-6 flex flex-col items-start text-left">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
              <Flame className="w-3.5 h-3.5 text-primary" /> For Fast-Growing Startups
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter uppercase leading-[1.05]">
              Viral Growth Loops.<br />
              <span className="text-gray-500">Sub-100ms Links.</span><br />
              Accelerate Launch.
            </h1>
            <p className="text-base sm:text-lg text-white/50 font-light max-w-xl leading-relaxed">
              Validate demand with high-converting pre-launch forms, deploy referral loops that turn users into advocates, and monitor paid ad channels with lightning-fast redirect metrics.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
              <Link href="/auth/sign-up" className="w-full sm:w-auto">
                <ShinyButton className="w-full justify-center py-4 px-8 text-base">
                  Accelerate Startup Launch <ArrowRight className="w-4 h-4 ml-1" />
                </ShinyButton>
              </Link>
              <a href="#benefits" className="w-full sm:w-auto text-center py-3.5 px-8 rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/20 transition-all font-semibold text-sm">
                Explore Referral loops
              </a>
            </div>
          </div>

          {/* Right Column: Dashboard Preview */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
            <div className="w-full max-w-[460px] relative z-10">
              <DashboardPreview sectionId="referrals" className="shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-24 border-t border-white/5 relative z-10">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="space-y-4 mb-16 text-center">
            <span className="text-[10px] font-bold tracking-[0.2em] text-primary uppercase">Growth Pipeline Tools</span>
            <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">
              Turn click volume into organic conversion.
            </h2>
            <p className="text-sm sm:text-base text-white/40 max-w-lg mx-auto font-light">
              Built to validate quickly and scale smoothly under sudden traffic spikes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Card 1: Waitlists */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <Share2 className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Viral Milestone Waitlists</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Incentivize organic word-of-mouth. Set up rewards, coupon thresholds, or download keys that unlock programmatically as signups complete user loops.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Direct referral queue skip triggers', 'Auto-generated invite URLs', 'Double opt-in signup filters'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Card 2: Speed redirects */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Sub-100ms Edge Redirection</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Every millisecond of delay costs conversions on paid Google Ads. Our URL routing architecture runs at the edge, returning responses in under 100ms.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Ultra-low latency routing servers', 'Anti-bot crawler filter blocks', 'Real-time campaign parameter forwards'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Card 3: Validation Forms */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <FormInput className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Rapid Lead Capture Forms</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Don't waste time writing landing code. Build newsletter collection fields, customer polls, or beta feedback questionnaires using our visual forms generator.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Lead analytics tracking tables', 'Direct webhook push updates', 'Custom form color customizer suites'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Card 4: Developer APIs */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <Code className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Developer First Integrations</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Programmatic control is built-in. Create campaign tracking links, capture customer profiles, and trigger referral checks directly from your application code.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['REST API key management panels', 'Real-time JSON payload outputs', 'Comprehensive code guides'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 border-t border-white/5 relative z-10 bg-black/40">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">
              Startup FAQs
            </h2>
            <p className="text-xs sm:text-sm text-white/40 max-w-sm mx-auto font-light">
              Understand our referral and redirection features.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => {
              const isOpen = openFaq === idx
              return (
                <div 
                  key={idx}
                  className="bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden transition-all duration-300"
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
                  >
                    <span className="text-sm font-bold uppercase tracking-tight text-white/90">
                      {faq.q}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-white/40 transition-transform duration-300 ${isOpen ? "rotate-180 text-primary" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="px-5 pb-5 pt-1 text-xs sm:text-sm text-white/50 leading-relaxed font-light border-t border-white/[0.02]">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-32 border-t border-white/5 relative z-10 overflow-hidden text-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-primary/10 rounded-full blur-[160px] pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 relative space-y-8">
          <h2 className="text-4xl sm:text-6xl font-bold tracking-tighter uppercase">
            Build your launch momentum.
          </h2>
          <p className="text-sm sm:text-base text-white/40 max-w-md mx-auto font-light leading-relaxed">
            Validate products fast and configure organic waitlist refer-a-friend loops in minutes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/auth/sign-up">
              <ShinyButton className="px-10 py-4 text-sm font-bold">
                Deploy Waitlist Free <ArrowRight className="w-4 h-4 ml-1" />
              </ShinyButton>
            </Link>
            <Link href="/pricing" className="text-white/60 hover:text-white border border-white/10 px-8 py-3.5 rounded-full text-sm font-semibold hover:border-white/20 transition-all">
              Compare Pricing
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
